import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import InboxOutlined from '@mui/icons-material/InboxOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Fragment, type ReactNode, useState } from 'react';
import { downloadCsv } from '../creatives/data/csv.js';
import { ExportCsvButton } from '../creatives/ExportCsvButton.js';
import { OutcomeDrawer } from './OutcomeDrawer.js';
import { outcomeIcon } from './outcomeIcon.js';
import { outcomesToCsv } from './outcomeRows.js';
import type { ReportScope } from './reportMeta.js';
import type { MapOutcome, MapPanel, MapRow, MapStatus } from './sessionRows.js';
import { outcomeKeyOf, outcomesOf } from './sessionRows.js';

const RAIL_WIDTH = 330;
const MARK_SIZE = 28;
const CONNECTOR_HEIGHT = 24;
const LINE_CLAMP = 3;
const OUTCOME_ICON_SIZE = 22;

const LINE_COLOR: Record<MapStatus, string> = {
  done: 'success.main',
  active: 'primary.main',
  pending: 'text.disabled',
};

const EDIT_LINK_SX = {
  fontSize: 12,
  '&:disabled': { color: 'text.disabled', textDecoration: 'none', cursor: 'default' },
};

interface SessionMapProps {
  rows: MapRow[];
  saved?: MapOutcome[];
  scope: ReportScope | null;
  leadingKpi: string;
  onEdit: () => void;
  editDisabled: boolean;
  footer?: ReactNode;
}

interface MarkProps {
  index: number;
  status: MapStatus;
}

interface PanelProps {
  panel: MapPanel;
  title: string;
  onEdit: () => void;
  editDisabled: boolean;
}

interface OutcomeCardProps {
  outcome: MapOutcome;
  onOpen: () => void;
}

function SectionHeader({ title, note }: { title: string; note?: string }): ReactNode {
  return (
    <Stack sx={{ gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: 'text.secondary',
        }}
      >
        {title}
      </Typography>
      {note === undefined ? null : (
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{note}</Typography>
      )}
    </Stack>
  );
}

function Mark({ index, status }: MarkProps): ReactNode {
  return (
    <Box
      sx={{
        width: MARK_SIZE,
        height: MARK_SIZE,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        border: '1px solid',
        borderColor: status === 'done' ? 'success.main' : 'text.primary',
        bgcolor: status === 'active' ? 'text.primary' : 'transparent',
        color:
          status === 'active'
            ? 'background.paper'
            : status === 'done'
              ? 'success.main'
              : 'text.primary',
        ...(status === 'pending' ? { borderColor: 'divider', color: 'text.disabled' } : {}),
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'inherit' }}>{index}</Typography>
    </Box>
  );
}

function Panel({ panel, title, onEdit, editDisabled }: PanelProps): ReactNode {
  const [open, setOpen] = useState(true);

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        px: 3,
        py: 2,
        backgroundColor: 'white',
      }}
    >
      <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
        <ButtonBase
          onClick={() => setOpen(!open)}
          sx={{ borderRadius: '50%', p: 0.5 }}
          aria-expanded={open}
          aria-label={panel.label ?? title}
        >
          <ExpandMoreOutlined
            sx={{
              fontSize: 16,
              color: 'text.secondary',
              transform: open ? 'none' : 'rotate(-90deg)',
            }}
          />
        </ButtonBase>
        {panel.label === null ? (
          <Box sx={{ flex: 1 }} />
        ) : (
          <Typography
            sx={{
              flex: 1,
              fontSize: 10,
              fontWeight: 700,
              color: 'text.secondary',
            }}
          >
            {panel.label.toUpperCase()}
          </Typography>
        )}
        <Link
          component="button"
          type="button"
          onClick={onEdit}
          disabled={editDisabled}
          sx={EDIT_LINK_SX}
        >
          Edit
        </Link>
      </Stack>
      <Collapse in={open}>
        <Typography sx={{ pl: 5, pt: 1, fontSize: 12, color: 'text.secondary' }}>
          {panel.parts.map((part, index) => (
            <Fragment key={part.text}>
              {index === 0 ? null : ' · '}
              {part.tooltip === null ? (
                part.text
              ) : (
                <Tooltip title={part.tooltip}>
                  <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                    {part.text}
                  </Box>
                </Tooltip>
              )}
            </Fragment>
          ))}
        </Typography>
      </Collapse>
    </Box>
  );
}

function OutcomeCard({ outcome, onOpen }: OutcomeCardProps): ReactNode {
  const Icon = outcomeIcon(outcome.kind);

  return (
    <ButtonBase
      onClick={onOpen}
      data-testid={`outcome-card-${outcome.runId}`}
      sx={{
        display: 'flex',
        gap: 2.5,
        px: 3,
        py: 2.5,
        textAlign: 'left',
        alignItems: 'center',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Icon sx={{ fontSize: OUTCOME_ICON_SIZE, color: 'text.secondary', flexShrink: 0 }} />
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {outcome.date ? `${outcome.name} ${outcome.date}` : outcome.name}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', textTransform: 'capitalize' }}>
          {outcome.kind}
        </Typography>
      </Stack>
      <OpenInNewOutlined sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
    </ButtonBase>
  );
}

export function SessionMap({
  rows,
  saved = [],
  scope,
  leadingKpi,
  onEdit,
  editDisabled,
  footer = null,
}: SessionMapProps): ReactNode {
  const [opened, setOpened] = useState<MapOutcome | null>(null);
  const outcomes = outcomesOf(rows, saved);

  return (
    <Stack
      data-testid="session-map"
      sx={{
        width: RAIL_WIDTH,
        flexShrink: 0,
        gap: 5,
        px: 4,
        py: 5,
        borderLeft: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack sx={{ gap: 3 }}>
        <SectionHeader title="SESSION MAP" />

        <Stack>
          {rows.map((row, index) => (
            <Stack key={row.key} direction="row" sx={{ gap: 2 }}>
              <Stack sx={{ alignItems: 'center', alignSelf: 'stretch' }}>
                <Mark index={index + 1} status={row.status} />
                {index === rows.length - 1 ? null : (
                  <Box
                    sx={{
                      flex: 1,
                      width: '1px',
                      minHeight: CONNECTOR_HEIGHT,
                      bgcolor: 'divider',
                    }}
                  />
                )}
              </Stack>

              <Box
                data-testid={`session-map-row-${index}`}
                sx={{ flex: 1, minWidth: 0, py: 0.5, pb: index === rows.length - 1 ? 0 : 3 }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                  {row.title}
                </Typography>
                {row.line === null ? null : (
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: LINE_COLOR[row.status],
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: LINE_CLAMP,
                      overflow: 'hidden',
                    }}
                  >
                    {row.line}
                  </Typography>
                )}

                {row.panel === null || !row.editable ? null : (
                  <Panel
                    panel={row.panel}
                    title={row.title}
                    onEdit={onEdit}
                    editDisabled={editDisabled}
                  />
                )}
              </Box>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Divider />

      <Stack sx={{ gap: 3 }}>
        <Stack direction="row" sx={{ gap: 2, alignItems: 'flex-start' }}>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <SectionHeader
              title="OUTCOMES GENERATED"
              note="Outcomes appear here as they are created."
            />
          </Stack>
          {outcomes.length === 0 ? null : (
            <ExportCsvButton
              dataTest="export-session-outcomes"
              label="Export list"
              onClick={() => downloadCsv('outcomes.csv', outcomesToCsv(outcomes))}
            />
          )}
        </Stack>

        {outcomes.length === 0 ? (
          <Stack sx={{ gap: 1, py: 5, alignItems: 'center' }}>
            <InboxOutlined sx={{ fontSize: 22, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>No outcomes yet</Typography>
          </Stack>
        ) : (
          outcomes.map((outcome) => (
            <OutcomeCard
              key={outcomeKeyOf(outcome)}
              outcome={outcome}
              onOpen={() => setOpened(outcome)}
            />
          ))
        )}
      </Stack>

      {footer}

      <OutcomeDrawer
        outcome={opened}
        scope={scope}
        leadingKpi={leadingKpi}
        onClose={() => setOpened(null)}
      />
    </Stack>
  );
}
